<template>

    <validation-observer vid="settings" ref="settingsObserver" v-slot="{ invalid, errors }">

        <v-container fluid class="pa-5">

            <v-row>

                <!-- SETTINGS -->
                <v-col cols="6">
                    <v-container class="pa-0">

                        <v-row dense>
                            <v-col cols="12">

                                <title-card title="Settings" help="subpanel-settings-settings">

                                    <validation-provider
                                        mode="lazy"
                                        name="sub panel name"
                                        rules="required|min:5|max:50"
                                        v-slot="{ errors }">

                                        <v-text-field
                                            v-model="data.name"
                                            :counter="100"
                                            label="Sub panel name"
                                            placeholder="Enter sub panel name"
                                            :error-messages="errors"
                                            @change="emit"
                                        ></v-text-field>

                                    </validation-provider>

                                    <validation-provider
                                        mode="lazy"
                                        name="description"
                                        rules="max:50"
                                        v-slot="{ errors }">

                                        <v-textarea
                                            :counter="1000"
                                            v-model="data.description"
                                            label="Sub panel description"
                                            placeholder="Enter sub panel description"
                                            :error-messages="errors"
                                            :rows="1"
                                            @change="emit"
                                        ></v-textarea>

                                    </validation-provider>

                                    <validation-provider
                                        mode="lazy"
                                        name="community url"
                                        :rules="data.url ? 'url:max:100' : 'required|url|max:1000'"
                                        v-slot="{ errors }">

                                        <v-scale-transition>
                                            <v-text-field
                                                v-model="data.url"
                                                v-show="!data.app_url || !data.app_enabled"
                                                :counter="250"
                                                label="Community URL"
                                                placeholder="Enter community URL"
                                                :error-messages="errors"
                                                @change="emit"
                                            ></v-text-field>
                                        </v-scale-transition>

                                    </validation-provider>

                                    <validation-provider
                                        name="support email"
                                        mode="lazy"
                                        rules="required|email|max:100"
                                        v-slot="{ errors }">

                                        <v-text-field
                                            v-model="data.support_email"
                                            :counter="100"
                                            label="Support email address"
                                            placeholder="Enter support email"
                                            :error-messages="errors"
                                            @change="emit"
                                        ></v-text-field>

                                    </validation-provider>

                                    <v-select class="mt-5"
                                              :items="ages"
                                              v-model="data.minimum_age_to_join"
                                              label="Minimum member age"
                                              @change="emit"
                                              dense>
                                    </v-select>

                                    <v-switch class="mt-2"
                                              v-model="data.app_enabled"
                                              @change="emit"
                                              label="Enable Members App"
                                              hide-details
                                    ></v-switch>

                                    <v-scale-transition>
                                        <v-switch class="mt-4"
                                                  v-show="data.app_enabled"
                                                  v-model="data.app_url"
                                                  @change="emit"
                                                  hide-details
                                                  label="Community URL points to the Members App"
                                        ></v-switch>
                                    </v-scale-transition>

                                </title-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-col>

                <v-col cols="6">

                    <v-container class="pa-0">

                        <!-- MANAGER SELECTION -->
                        <v-row dense>
                            <v-col cols="12">
                                <title-card title="Manager" help="subpanel-settings-manager">

                                    <validation-provider
                                        ref="manager"
                                        name="manager"
                                        rules="required"
                                        v-slot="{ errors }">

                                        <user-picker
                                            v-model="data.manager"
                                            :exclude="data.manager"
                                            menu-title="Select manager"
                                            :roles="['Panel Manager']"
                                            :clearable="false"
                                            :error-messages="errors"
                                            @input="emit"
                                        ></user-picker>

                                    </validation-provider>
                                </title-card>
                            </v-col>

                            <!-- DYNAMIC PROFILING -->
                            <v-col cols="12">
                                <title-card title="Dynamic profiling"
                                            help="subpanel-settings-dynamic-profiling"
                                            :hide-contents="!data.dynamic_profiling_enabled">

                                    <template v-slot:buttons>
                                        <v-tooltip left color="primary">
                                            <template v-slot:activator="{ on }">
                                                <v-sheet v-on="on" color="transparent">
                                                    <v-switch v-model="data.dynamic_profiling_enabled"
                                                              class="mt-4"
                                                              dense
                                                              @change="emit"
                                                    ></v-switch>
                                                </v-sheet>
                                            </template>
                                            <span>Turn dynamic profiling on/off</span>
                                        </v-tooltip>
                                    </template>

                                    <v-container fluid>
                                        <v-row dense>
                                            <v-col cols="4" md="4" sm="12">

                                                <validation-provider
                                                    mode="lazy"
                                                    name="dynamic profiling compensation"
                                                    rules="required|integer|min_value:0|max_value:10000"
                                                    v-slot="{ errors }">

                                                    <v-text-field
                                                        v-model="data.dp_compensation_points"
                                                        label="Compensation"
                                                        placeholder="Enter compensation in reward points"
                                                        :error-messages="errors"
                                                        @change="emit"
                                                        persistent-hint
                                                        prepend-icon="mdi-currency-sign"
                                                    ></v-text-field>
                                                </validation-provider>
                                            </v-col>

                                            <v-col cols="8" md="8" sm="12">
                                                <v-slider
                                                    v-model="data.dp_max_questions"
                                                    :min="1"
                                                    :max="10"
                                                    ticks
                                                    hint="Max # of profiling questions per session"
                                                    persistent-hint
                                                    thumb-label="always"
                                                    :thumb-size="30"
                                                    class="mt-8"
                                                    @change="emit"
                                                ></v-slider>
                                            </v-col>
                                        </v-row>
                                    </v-container>

                                </title-card>
                            </v-col>

                            <!-- GOOGLE ANALYTICS -->
                            <v-col cols="12">
                                <title-card title="Google Analytics"
                                            help="subpanel-settings-google-analytics"
                                            :hide-contents="!data.ga_enabled">

                                    <template v-slot:buttons>
                                        <v-tooltip left color="primary">
                                            <template v-slot:activator="{ on }">
                                                <v-sheet v-on="on" color="transparent">
                                                    <v-switch v-model="data.ga_enabled"
                                                              class="mt-4"
                                                              dense
                                                              @change="emit"
                                                    ></v-switch>
                                                </v-sheet>
                                            </template>
                                            <span>Turn dynamic profiling on/off</span>
                                        </v-tooltip>
                                    </template>


                                    <v-scale-transition>

                                        <v-text-field v-show="data.ga_enabled"
                                                      v-model="data.ga_ua"
                                                      class="mt-0"
                                                      prefix="UA -"
                                                      @change="emit"
                                                      hide-details
                                                      label="Universal Analytics ID"
                                                      placeholder="XXXXXXXXX-X"
                                        ></v-text-field>
                                    </v-scale-transition>

                                </title-card>
                            </v-col>

                        </v-row>
                    </v-container>
                </v-col>

                <!-- LOCALES -->
                <v-col cols="12">

                    <title-card title="Selected locales" help="subpanel-settings-selected-locales">

                        <validation-provider
                            ref="locales"
                            name="locales"
                            rules="required"
                            v-slot="{ errors }">

                            <locale-selector
                                v-model="data.locales"
                                :panel-locales="panelLocales"
                                :error-messages="errors"
                                :translate="false"
                                @input="emit"
                            ></locale-selector>

                        </validation-provider>
                    </title-card>

                </v-col>

                <v-col cols="12">
                    <v-btn text @click="deleteClicked">Delete sub panel</v-btn>
                </v-col>
            </v-row>
        </v-container>

        <confirmation-dialog v-model="deleteConfirmationDialogModel"
                             headline="DELETE CONFIRMATION"
                             delete
                             confirm-button-text="Delete"
                             abort-button-text="Cancel"
                             @selected="deleteConfirmationClicked">
            Deleting sub panel is potentially very bad thing to do. You cannot delete a sub panel with members.
            You should move all your existing panelists to an another sub panel
            before attempting to delete.
        </confirmation-dialog>

        <json v-if="false" :data="data"></json>

    </validation-observer>

</template>

<script>

import UserPicker from '../../../../components/UI/UserPicker';
import LocaleSelector from '../../../../components/UI/LocaleSelector';
import Json from 'vue-json-pretty';
import TitleCard from "../../../../components/UI/TitleCard";
import {extend} from 'vee-validate/dist/vee-validate.full';
import ConfirmationDialog from "../../../../components/UI/ConfirmationDialog";

export default {

    components: {
        UserPicker,
        LocaleSelector,
        Json,
        TitleCard,
        ConfirmationDialog,
    },

    props: {
        subPanel: {
            type: Object,
            required: false,
            default: () => ({
                name: 'New sub panel',
            })
        },
    },

    watch: {
        subPanel(newData) {
            this.data = Object.assign({}, this.data, newData);
        },
    },

    data() {
        return {
            deleteConfirmationDialogModel: false,
            data: {
                name: null,
                description: null,
                url: null,
                locales: [],
                manager: null,
                dp_compensation_points: 0,
                dp_max_questions: 3,
                dynamic_profiling_enabled: true,
                app_enabled: false,
                app_url: false,
                ga_enabled: false,
                ga_ua: null,
            },
            localePickerModel: false,
            localizationEditorModel: false,
        }
    },

    computed: {

        panelLocales() {
            if (this.$store.state.PanelSettings.settings.locales) {
                return this.$store.state.PanelSettings.settings.locales;
            }
            return [];
        },

        ages() {
            let ages = [];
            for (let age = 12; age <= 100; age++) {
                ages.push(age);
            }
            return ages;
        }
    },

    created() {

        extend('url', {
            validate(value) {
                return new RegExp('^https:\/\/[0-9a-z-.]+\.[a-z]+$').test(value);
            },
            message: "Valid URL is required and must begin with https://"
        });

        this.data = Object.assign(this.data, this.subPanel);
    },

    methods: {

        translationsClicked() {
            this.localizationEditorModel = true;
        },

        deleteClicked() {
            this.deleteConfirmationDialogModel = true;
        },

        deleteConfirmationClicked(bool) {
            this.deleteConfirmationDialogModel = false;

            if (bool) {
                this.$emit('delete', this.data.id);
            }
        },

        emit() {
            this.$emit('change', this.data);
        },
    }
}
</script>
